/**
 * SKE-4881 — how far down the page the sticky banner layer reaches.
 *
 * The dashboard panel is the page scroller and the shell's banner is
 * `sticky top-0 z-50` inside it, so anything else in that panel that is
 * legitimately sticky has to start BELOW the banner or it spends the whole
 * scroll underneath one. The Insights toolbar is the case that was reported:
 * stuck at `top: 16px` on a lower layer, its account picker, date range and
 * Create report were completely covered.
 *
 * The height cannot be a constant. It depends on which of the four banners
 * won the slot and on how that banner's copy wraps, which changes with the
 * viewport. So it is measured from the rendered banner and published as a
 * custom property, the same mechanism the shell already uses for
 * `--header-height`, and the sticky surfaces underneath add it to their own
 * offset.
 *
 * THE DEFAULT IS THE WHOLE SAFETY ARGUMENT. Most customers see no banner at
 * all. While no banner is mounted the property is not set, so every consumer
 * has to read it as `var(--banner-height, 0px)` and gets exactly today's
 * layout. The property is removed, never zeroed, so an absent measurement and
 * a deliberate zero are the same thing to a consumer.
 *
 * It is also published BEFORE paint. A ref callback runs in the commit phase,
 * and `getBoundingClientRect` there forces layout, so the first value is
 * available in the same frame the banner first appears. The observer that
 * follows only handles reflows. Nothing waits for a second frame, so the
 * toolbar never jumps.
 *
 * Every banner that wants the slot renders on the first commit, before the
 * precedence claims settle and the losers unmount. So more than one banner
 * can be attached for a moment, in any order. Each attached root is kept in a
 * module-level registry and the property is always rewritten from that
 * registry, so a loser detaching removes only its own entry and never the
 * winner's value.
 */

import { useCallback, useRef } from "react";

/** Read it as `var(--banner-height, 0px)`. Absent means no banner. */
export const BANNER_HEIGHT_PROPERTY = "--banner-height";

const attached = new Map<HTMLElement, number>();

function sync(): void {
  if (attached.size === 0) {
    document.body.style.removeProperty(BANNER_HEIGHT_PROPERTY);
    return;
  }
  // Two entries only coexist while losers unmount; the tallest never leaves
  // the toolbar under whichever banner is actually showing.
  const height = Math.max(...attached.values());
  document.body.style.setProperty(
    BANNER_HEIGHT_PROPERTY,
    `${Math.round(height)}px`
  );
}

/**
 * Returns a ref to hang on a banner's ROOT element.
 *
 * Once the slot settles only the winning banner is attached, so the property
 * describes the banner the customer can actually see. A banner that lost the
 * slot removes only its own entry when it unmounts.
 */
export function useBannerHeightProperty(): (node: HTMLElement | null) => void {
  const observerRef = useRef<ResizeObserver | null>(null);
  const nodeRef = useRef<HTMLElement | null>(null);

  return useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (nodeRef.current) {
      attached.delete(nodeRef.current);
      nodeRef.current = null;
    }

    if (!node) {
      sync();
      return;
    }

    nodeRef.current = node;
    attached.set(node, node.getBoundingClientRect().height);
    sync();

    // jsdom has no ResizeObserver, and neither do the oldest browsers we still
    // serve. The measurement above already happened, so the fallback is a
    // correct offset that does not follow a reflow rather than no offset.
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      if (!attached.has(node)) return;
      attached.set(node, node.getBoundingClientRect().height);
      sync();
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);
}
